import { indexAtTime, timeAtIndex } from '../utils/helpers';

const HOUR_MS = 1000 * 60 * 60;
const DAY_MS = HOUR_MS * 24;
const YEAR_MS = DAY_MS * 365.25;

/**
 * Localised "Jan", "Feb", … via `Intl.DateTimeFormat` rather than a
 * hard-coded array. Cached because constructing a formatter on every
 * tick would be wasteful, and centralised so swapping to a runtime
 * locale (e.g. `i18next.language`) later is a one-line change.
 *
 * Day/month/year ticks render in UTC (tz-independent calendar dates).
 * Intraday (hour-kind) ticks render in the browser's local timezone to
 * match the v1 chart — so a user sees their own wall-clock hours, not
 * UTC — hence the second, local-zone formatter for their date boundary.
 */
const monthShortFormatter = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  timeZone: 'UTC',
});
const monthShortFormatterLocal = new Intl.DateTimeFormat('en-US', {
  month: 'short',
});

const formatMonthShort = (d: Date): string => monthShortFormatter.format(d);
const formatMonthShortLocal = (d: Date): string =>
  monthShortFormatterLocal.format(d);

const pad2 = (n: number) => n.toString().padStart(2, '0');

export interface XAxisTick {
  ms: number;
  label: string;
  highlight?: boolean;
}

type TickUnit =
  | { kind: 'hour'; stepHours: number }
  | { kind: 'day'; stepDays: number }
  | { kind: 'month'; stepMonths: number };

const pickUnit = (spanMs: number): TickUnit => {
  const days = spanMs / DAY_MS;
  const years = spanMs / YEAR_MS;

  if (spanMs <= 2 * DAY_MS) {
    return { kind: 'hour', stepHours: 1 };
  }

  if (days <= 5) {
    return { kind: 'hour', stepHours: 4 };
  }

  if (days <= 21) {
    return { kind: 'day', stepDays: 1 };
  }

  if (days < 42) {
    return { kind: 'day', stepDays: 7 };
  }

  if (days <= 370) {
    return { kind: 'month', stepMonths: 1 };
  }

  if (years <= 3) {
    return { kind: 'month', stepMonths: 3 };
  }

  if (years <= 5) {
    return { kind: 'month', stepMonths: 4 };
  }

  if (years <= 10) {
    return { kind: 'month', stepMonths: 6 };
  }

  if (years <= 15) {
    return { kind: 'month', stepMonths: 12 };
  }

  if (years <= 30) {
    return { kind: 'month', stepMonths: 24 };
  }

  if (years <= 45) {
    return { kind: 'month', stepMonths: 36 };
  }

  if (years <= 65) {
    return { kind: 'month', stepMonths: 48 };
  }

  if (years <= 85) {
    return { kind: 'month', stepMonths: 60 };
  }

  return { kind: 'month', stepMonths: 120 };
};

export const xAxisLabelsAreLocal = (spanMs: number): boolean =>
  pickUnit(spanMs).kind === 'hour';

// Intraday ticks anchor to local wall-clock hours (v1 parity): floor to the
// browser's local hour boundary so labels read in the user's timezone.
const floorToHour = (ms: number, stepHours: number): number => {
  const d = new Date(ms);
  const h = Math.floor(d.getHours() / stepHours) * stepHours;

  return new Date(
    d.getFullYear(),
    d.getMonth(),
    d.getDate(),
    h,
    0,
    0,
    0
  ).getTime();
};

const floorToDay = (ms: number): number => {
  const d = new Date(ms);

  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
};

const floorToMonday = (ms: number): number => {
  const d = new Date(ms);
  const dayOfWeek = d.getUTCDay(); // 0 Sun .. 6 Sat
  const daysFromMonday = (dayOfWeek + 6) % 7;

  return Date.UTC(
    d.getUTCFullYear(),
    d.getUTCMonth(),
    d.getUTCDate() - daysFromMonday
  );
};

const floorToMonth = (ms: number, stepMonths: number): number => {
  const d = new Date(ms);

  if (stepMonths >= 12) {
    const yearStep = Math.floor(stepMonths / 12);
    const y = Math.floor(d.getUTCFullYear() / yearStep) * yearStep;

    return Date.UTC(y, 0, 1);
  }

  const m = Math.floor(d.getUTCMonth() / stepMonths) * stepMonths;

  return Date.UTC(d.getUTCFullYear(), m, 1);
};

const advance = (ms: number, unit: TickUnit): number => {
  const d = new Date(ms);

  if (unit.kind === 'hour') {
    return ms + unit.stepHours * HOUR_MS;
  }

  if (unit.kind === 'day') {
    return Date.UTC(
      d.getUTCFullYear(),
      d.getUTCMonth(),
      d.getUTCDate() + unit.stepDays
    );
  }

  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + unit.stepMonths, 1);
};

const firstTick = (domainStart: number, unit: TickUnit): number => {
  if (unit.kind === 'hour') {
    return floorToHour(domainStart, unit.stepHours);
  }

  if (unit.kind === 'day') {
    return unit.stepDays === 7
      ? floorToMonday(domainStart)
      : floorToDay(domainStart);
  }

  return floorToMonth(domainStart, unit.stepMonths);
};

const labelFor = (
  d: Date,
  prev: Date | null,
  unit: TickUnit
): { label: string; highlight: boolean } => {
  if (unit.kind === 'hour') {
    // Intraday: read everything in local time so the hours match the user's
    // clock (v1 parity). Positioning still uses the absolute `ms`, so only the
    // label zone changes.
    const dayChanged = !prev || prev.getDate() !== d.getDate();

    if (dayChanged) {
      return {
        label: `${formatMonthShortLocal(d)} ${d.getDate()}`,
        highlight: true,
      };
    }

    return {
      label: `${pad2(d.getHours())}:${pad2(d.getMinutes())}`,
      highlight: false,
    };
  }

  if (unit.kind === 'day') {
    const yearChanged = !!prev && prev.getUTCFullYear() !== d.getUTCFullYear();
    const monthChanged = !!prev && prev.getUTCMonth() !== d.getUTCMonth();

    if (yearChanged) {
      return { label: String(d.getUTCFullYear()), highlight: true };
    }

    if (monthChanged) {
      return { label: formatMonthShort(d), highlight: true };
    }

    return { label: String(d.getUTCDate()), highlight: false };
  }

  if (unit.stepMonths >= 12) {
    return { label: String(d.getUTCFullYear()), highlight: false };
  }

  // Label the January tick with the year (v1's year-boundary highlight).
  // Key off the tick's own month — not "is this the first tick?" — so a range
  // that never crosses January (e.g. Apr–Jun) shows month names throughout
  // instead of forcing the leading tick to the year ("2026" → "Apr").
  if (d.getUTCMonth() === 0) {
    return { label: String(d.getUTCFullYear()), highlight: true };
  }

  return { label: formatMonthShort(d), highlight: false };
};

/** Tick-unit bucket for a timestamp; two bars with different buckets have a
 * unit boundary between them. */
const bucketOf = (ms: number, unit: TickUnit): number => {
  if (unit.kind === 'hour') {
    return floorToHour(ms, unit.stepHours);
  }

  if (unit.kind === 'day') {
    return unit.stepDays === 7 ? floorToMonday(ms) : floorToDay(ms);
  }

  return floorToMonth(ms, unit.stepMonths);
};

/**
 * Bar-anchored ticks, mirroring v1's `createTicksFromRange`: walk the
 * visible bars and emit a tick at the first bar past each unit boundary,
 * positioned at the bar's own time. On an index (bar-timeline) x-scale,
 * wall-clock boundary ticks would be wrong twice over: off-market gaps
 * are pixel-compressed, so the boundaries inside a gap (e.g. every closed
 * hour overnight) pile up between two adjacent bars as a dense gridline
 * cluster, and a boundary label ("Jul 2") floats mid-gap next to the
 * wrong bar, sliding as the pan offset changes. Anchoring to bars pins
 * one tick — and the day-change label — to the first bar of the new
 * day/hour/month, exactly like v1.
 *
 * `prev` (day/month/year-change detection) seeds from the bar just before
 * the window — not `null` — so the leftmost tick doesn't masquerade as a
 * boundary label while panning. `timeAtIndex` extrapolates past either
 * end of the timeline, which keeps ticks flowing at bar spacing when the
 * chart is panned into the future (v1 did this with fake bars).
 */
const barAnchoredTicks = (
  domain: [number, number],
  tl: number[],
  unit: TickUnit
): XAxisTick[] => {
  const startIdx = Math.ceil(indexAtTime(tl, domain[0]));
  const endIdx = Math.floor(indexAtTime(tl, domain[1]));
  const ticks: XAxisTick[] = [];

  let prevBucket = bucketOf(timeAtIndex(tl, startIdx - 1), unit);
  let prev: Date | null = new Date(timeAtIndex(tl, startIdx - 1));

  for (let i = startIdx; i <= endIdx; i++) {
    const ms = timeAtIndex(tl, i);
    const bucket = bucketOf(ms, unit);

    if (bucket === prevBucket) {
      continue;
    }

    prevBucket = bucket;

    const d = new Date(ms);
    const { label, highlight } = labelFor(d, prev, unit);

    ticks.push({ ms, label, highlight });
    prev = d;
  }

  return ticks;
};

export const generateXAxisTicks = (
  domain: [number, number],
  barTimeline: number[]
): XAxisTick[] => {
  const span = domain[1] - domain[0];

  if (!Number.isFinite(span) || span <= 0) {
    return [];
  }

  const unit = pickUnit(span);

  if (barTimeline.length >= 2) {
    return barAnchoredTicks(domain, barTimeline, unit);
  }

  const ticks: XAxisTick[] = [];

  let current = firstTick(domain[0], unit);

  while (current < domain[0]) {
    current = advance(current, unit);
  }

  let prev: Date | null = null;

  while (current <= domain[1]) {
    const d = new Date(current);
    const { label, highlight } = labelFor(d, prev, unit);

    ticks.push({ ms: current, label, highlight });
    prev = d;
    current = advance(current, unit);
  }

  return ticks;
};
